import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Gauge, 
  TrendingDown, 
  Users, 
  AlertTriangle, 
  CheckCircle2, 
  Truck,
  Car,
  Package,
  Building2,
  ShieldCheck,
  Clock,
  DollarSign,
  Smartphone,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    fleetSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/demo-request`, formData);
      toast.success('Thank you! We\'ll contact you within 24 hours.');
      setFormData({ name: '', company: '', phone: '', email: '', fleetSize: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const painPoints = [
    { icon: AlertTriangle, title: 'No Real-Time Visibility', desc: 'Lost track of where your vehicles are?' },
    { icon: TrendingDown, title: 'Fuel Wastage', desc: 'Rising fuel costs eating into profits?' },
    { icon: Users, title: 'Poor Driver Behavior', desc: 'Speeding, harsh braking, idling?' },
    { icon: Clock, title: 'Manual Reporting', desc: 'Wasting hours on paperwork?' },
    { icon: ShieldCheck, title: 'Compliance Issues', desc: 'Struggling with SIRA regulations?' }
  ];

  const solutions = [
    { icon: MapPin, title: 'Live Vehicle Location', desc: 'Track every vehicle in real-time on interactive maps' },
    { icon: Gauge, title: 'Route Optimization', desc: 'Reduce travel time and fuel consumption' },
    { icon: TrendingDown, title: 'Fuel & Idle Monitoring', desc: 'Cut costs by monitoring fuel usage and idle time' },
    { icon: Users, title: 'Driver Behavior Analysis', desc: 'Improve safety with detailed driver scorecards' },
    { icon: AlertTriangle, title: 'Instant Alerts', desc: 'Get notified of speeding, geofence violations, and more' },
    { icon: Smartphone, title: 'Mobile App + Web Dashboard', desc: 'Access your fleet data anywhere, anytime' }
  ];

  const usps = [
    { icon: MapPin, title: 'UAE-Focused & Compliance-Ready', desc: 'Built for local regulations including SIRA requirements' },
    { icon: Truck, title: 'Scales 1 to 1000+ Vehicles', desc: 'Perfect for small businesses and large enterprises' },
    { icon: DollarSign, title: 'Simple Monthly Pricing', desc: 'No hidden fees, no complicated contracts' },
    { icon: Phone, title: 'Dedicated Support', desc: 'Local UAE support team ready to help' },
    { icon: ShieldCheck, title: 'Reliable Platform', desc: 'Enterprise-grade hardware and stable software' },
    { icon: Users, title: 'Fleet Manager Friendly', desc: 'Designed by fleet experts for fleet managers' }
  ];

  const useCases = [
    { 
      icon: Package, 
      title: 'Logistics & Delivery', 
      desc: 'Optimize delivery routes and track shipments in real-time',
      image: 'https://images.unsplash.com/photo-1751218682008-f68d3eff2d7c'
    },
    { 
      icon: Car, 
      title: 'Rental Cars', 
      desc: 'Monitor vehicle usage and prevent unauthorized movements',
      image: 'https://images.unsplash.com/photo-1587813369290-091c9d432daf'
    },
    { 
      icon: Building2, 
      title: 'Corporate Vehicles', 
      desc: 'Manage company cars and control business mileage',
      image: 'https://images.unsplash.com/photo-1755728531186-4efe610d8502'
    },
    { 
      icon: Truck, 
      title: 'Service Vans', 
      desc: 'Track field technicians and optimize service calls',
      image: 'https://images.pexels.com/photos/15379824/pexels-photo-15379824.jpeg'
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div 
          className="hero-background absolute inset-0 z-0"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'url(https://images.unsplash.com/photo-1661630802936-5ff15855cd1f)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-red-900/60 z-0" />
        
        <nav className="nav-bar-white relative z-20 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_099c2006-67c6-4d2e-acf9-1e0fd2c8352f/artifacts/80f6u7mg_Logo%20Final%20Whole%202026.png" 
              alt="TrackOn Logo" 
              className="h-12 object-contain"
            />
          </div>
          <div className="hidden md:flex gap-4">
            <Button variant="ghost" className="text-gray-700 hover:text-red-600 hover:bg-gray-100">
              Solutions
            </Button>
            <Button variant="ghost" className="text-gray-700 hover:text-red-600 hover:bg-gray-100">
              Pricing
            </Button>
            <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white">
              <Phone className="mr-2 h-4 w-4" /> Contact Us
            </Button>
          </div>
        </nav>

        <div className="hero-content relative z-10 max-w-7xl mx-auto px-8 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-6">
            <h1 className="hero-headline text-5xl md:text-6xl font-bold leading-tight">
              Complete Fleet Visibility, 
              <span className="text-red-500"> Anytime, Anywhere</span>
            </h1>
            <p className="hero-subheadline text-xl text-gray-200">
              Real-time GPS tracking, fuel monitoring, driver analytics, and route optimization—all in one platform built for UAE fleet owners.
            </p>
            <div className="price-highlight bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 inline-block">
              <p className="text-sm text-gray-300">Starting at</p>
              <p className="text-3xl font-bold text-red-500">AED 49 <span className="text-lg text-gray-300">/ vehicle / month</span></p>
            </div>
            <div className="flex flex-wrap gap-4 pt-4">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                Request Demo
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg">
                Talk to Us
              </Button>
            </div>
          </div>
          <div className="hero-image relative">
            <div className="dashboard-mockup relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_fleet-control-78/artifacts/scny39h2_dashboard-mockup.png" 
                alt="TrackOn Fleet Command Center - Real-Time Dashboard" 
                className="rounded-lg shadow-2xl border-4 border-white/20 hover:scale-105 transition-transform duration-500"
                style={{ transform: `translateY(${-scrollY * 0.2}px)` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="pain-points-section py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fleet Management Challenges Holding You Back?
            </h2>
            <p className="text-xl text-gray-600">
              We understand the daily struggles of fleet operators
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {painPoints.map((point, idx) => (
              <Card 
                key={idx} 
                className="pain-card hover:shadow-xl transition-all hover:-translate-y-2 border-gray-200 cursor-pointer"
              >
                <CardContent className="pt-6 text-center">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <point.icon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-sm text-gray-600">{point.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="solution-section py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How <span className="text-red-600">TrackOn</span> Solves This
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features designed for complete fleet control
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, idx) => (
              <div 
                key={idx} 
                className="solution-card group p-6 rounded-lg border-2 border-gray-200 hover:border-red-500 hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="bg-gradient-to-br from-red-500 to-red-700 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <solution.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-2">{solution.title}</h3>
                <p className="text-gray-600">{solution.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why TrackOn (USP) */}
      <section className="usp-section py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Why Fleet Managers Choose <span className="text-red-500">TrackOn</span>
            </h2>
            <p className="text-xl text-gray-300">
              Built specifically for UAE businesses with reliability at its core
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {usps.map((usp, idx) => (
              <div 
                key={idx} 
                className="usp-card bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 hover:bg-white/10 hover:border-red-500 transition-all hover:scale-105 cursor-pointer"
              >
                <div className="bg-red-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <usp.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">{usp.title}</h3>
                <p className="text-gray-400">{usp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section py-20 bg-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            No complicated slabs. No hidden fees. Just straightforward pricing.
          </p>
          <Card className="pricing-card border-4 border-red-500 shadow-2xl hover:scale-105 transition-transform">
            <CardContent className="p-12">
              <div className="mb-8">
                <p className="text-gray-600 text-lg mb-2">Starting from</p>
                <div className="text-6xl font-bold text-red-600 mb-2">
                  AED 49
                </div>
                <p className="text-xl text-gray-600">per vehicle / month</p>
              </div>
              <div className="space-y-3 mb-8 text-left">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">GPS Hardware + SIM Card Included</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Web Dashboard + Mobile App Access</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Professional Installation & Setup</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Dedicated Support Team</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">SIRA-Ready Infrastructure</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                * Custom pricing available for large fleets (100+ vehicles)
              </p>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-lg w-full">
                Get Custom Quote
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases-section py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Perfect For Every Fleet Type
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by diverse businesses across UAE
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, idx) => (
              <Card 
                key={idx} 
                className="use-case-card overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={useCase.image} 
                    alt={useCase.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 bg-red-600 w-12 h-12 rounded-lg flex items-center justify-center">
                    <useCase.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-xl text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-gray-600">{useCase.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Credibility */}
      <section className="trust-section py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted By Fleet Operators Across UAE
            </h2>
            <p className="text-xl text-gray-600">
              Enterprise-grade reliability you can count on
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <Card className="trust-card p-6 text-center hover:shadow-xl transition-all border-2 border-gray-200 hover:border-red-500">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">SIRA-Ready</h3>
              <p className="text-gray-600">Infrastructure compliant with UAE regulations</p>
            </Card>
            <Card className="trust-card p-6 text-center hover:shadow-xl transition-all border-2 border-gray-200 hover:border-red-500">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Data Security</h3>
              <p className="text-gray-600">Enterprise-grade encryption & protection</p>
            </Card>
            <Card className="trust-card p-6 text-center hover:shadow-xl transition-all border-2 border-gray-200 hover:border-red-500">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Local Support</h3>
              <p className="text-gray-600">Professional installation & 24/7 assistance</p>
            </Card>
            <Card className="trust-card p-6 text-center hover:shadow-xl transition-all border-2 border-gray-200 hover:border-red-500">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Proven Platform</h3>
              <p className="text-gray-600">Reliable hardware & stable software</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Strong CTA */}
      <section className="cta-section py-20 bg-gradient-to-br from-red-600 to-red-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1578496479939-722d9dd1cc5b)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="max-w-4xl mx-auto px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Take Control of Your Fleet Today
          </h2>
          <p className="text-xl mb-12 text-red-100">
            Join hundreds of UAE businesses managing their fleets smarter with TrackOn
          </p>
          
          <Card className="bg-white/95 backdrop-blur-md border-0 shadow-2xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Request Your Free Demo</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="text-left">
                    <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                    <Input 
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="text-left">
                    <Label htmlFor="company" className="text-gray-700">Company Name</Label>
                    <Input 
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="ABC Logistics"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="text-left">
                    <Label htmlFor="phone" className="text-gray-700">Phone Number *</Label>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+971 50 123 4567"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div className="text-left">
                    <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@company.com"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>
                <div className="text-left">
                  <Label htmlFor="fleetSize" className="text-gray-700">Fleet Size</Label>
                  <Input 
                    id="fleetSize"
                    name="fleetSize"
                    value={formData.fleetSize}
                    onChange={handleInputChange}
                    placeholder="e.g., 10 vehicles"
                    className="mt-1"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>
                <p className="text-sm text-gray-600 mt-4">
                  Our team will contact you within 24 hours
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer bg-gray-50 text-gray-800 py-12 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_099c2006-67c6-4d2e-acf9-1e0fd2c8352f/artifacts/80f6u7mg_Logo%20Final%20Whole%202026.png" 
                alt="TrackOn Logo" 
                className="h-10 mb-4 object-contain"
              />
              <p className="text-gray-600 text-sm">
                Professional GPS tracking and fleet management solutions for UAE businesses. Take control of your fleet with TrackOn.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-gray-900">Solutions</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="hover:text-red-600 cursor-pointer transition-colors">Fleet Tracking</li>
                <li className="hover:text-red-600 cursor-pointer transition-colors">Fuel Monitoring</li>
                <li className="hover:text-red-600 cursor-pointer transition-colors">Driver Analytics</li>
                <li className="hover:text-red-600 cursor-pointer transition-colors">Route Optimization</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-gray-900">Policies</h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>
                  <Link to="/warranty-policy" className="hover:text-red-600 transition-colors">
                    Warranty Policy
                  </Link>
                </li>
                <li>
                  <Link to="/service-policy" className="hover:text-red-600 transition-colors">
                    Service Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund-policy" className="hover:text-red-600 transition-colors">
                    Refund Policy
                  </Link>
                </li>
                <li className="hover:text-red-600 cursor-pointer transition-colors">Terms & Conditions</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-gray-900">Contact Us</h4>
              <ul className="space-y-3 text-gray-600 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-600" />
                  <span>+971 50 860 7793</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-red-600" />
                  <span>gps@nooryanivts.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-red-600" />
                  <span>Dubai, UAE</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-300 pt-8 text-center text-gray-600 text-sm">
            <p>© 2025 TrackOn. All rights reserved. | SIRA-Ready Infrastructure</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <a 
        href="https://wa.me/971508607793" 
        target="_blank" 
        rel="noopener noreferrer"
        className="whatsapp-button fixed bottom-8 right-8 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50 flex items-center justify-center"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle className="h-8 w-8" />
      </a>
    </div>
  );
};

export default LandingPage;
